/**
 * Shared shapes + parsers for connected-app calendar / mail tool results
 * (Nango + Merge Agent Handler). Snapshotted into Work chat artifacts.
 */

export interface ConnectedCalendarEvent {
  id: string;
  subject: string;
  start: string;
  end: string;
  organizer?: string;
  webLink?: string;
  isAllDay?: boolean;
  location?: string;
}

export interface ConnectedMailMessage {
  id: string;
  subject: string;
  from?: string;
  receivedAt?: string;
  preview?: string;
  webLink?: string;
  isRead?: boolean;
}

export interface ConnectedCalendarPayload {
  provider?: string;
  start?: string;
  end?: string;
  events: ConnectedCalendarEvent[];
  /** Merge Google fan-out: calendars queried (e.g. primary + secondary). */
  calendarIds?: string[];
  /** Empty-result / parse warning from the tool payload. */
  warning?: string;
}

export interface ConnectedMailPayload {
  provider?: string;
  query?: string;
  messages: ConnectedMailMessage[];
}

export interface ConnectedFileItem {
  id: string;
  name: string;
  path?: string;
  mimeType?: string;
  webLink?: string;
  modifiedAt?: string;
}

export interface ConnectedFilesPayload {
  provider?: string;
  query?: string;
  files: ConnectedFileItem[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const asBool = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const dateTimeField = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (isRecord(value)) {
    return asString(value.dateTime) ?? asString(value.date);
  }
  return undefined;
};

const organizerField = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (!isRecord(value)) return undefined;
  const emailAddress = value.emailAddress;
  if (isRecord(emailAddress)) {
    return asString(emailAddress.name) ?? asString(emailAddress.address);
  }
  return asString(value.email) ?? asString(value.displayName) ?? asString(value.name);
};

const normalizeCalendarEvent = (
  raw: Record<string, unknown>,
  index: number
): ConnectedCalendarEvent | null => {
  const id = asString(raw.id) ?? asString(raw.iCalUID) ?? `event-${index}`;
  const subject =
    asString(raw.subject) ??
    asString(raw.summary) ??
    asString(raw.title) ??
    'Untitled event';
  const start =
    dateTimeField(raw.start) ??
    asString(raw.start_time) ??
    asString(raw.startDateTime) ??
    '';
  const end =
    dateTimeField(raw.end) ?? asString(raw.end_time) ?? asString(raw.endDateTime) ?? '';
  if (!start && !end) return null;
  return {
    id,
    subject,
    start: start || end,
    end: end || start,
    organizer: organizerField(raw.organizer) ?? asString(raw.organizer_email),
    webLink: asString(raw.web_link) ?? asString(raw.webLink) ?? asString(raw.htmlLink),
    isAllDay: asBool(raw.is_all_day) ?? asBool(raw.isAllDay) ?? asBool(raw.allDay),
    location:
      asString(raw.location) ??
      (isRecord(raw.location) ? asString(raw.location.displayName) : undefined),
  };
};

const normalizeMailMessage = (
  raw: Record<string, unknown>,
  index: number
): ConnectedMailMessage | null => {
  const id = asString(raw.id) ?? `message-${index}`;
  const subject = asString(raw.subject) ?? '(No subject)';
  const from =
    asString(raw.from) ??
    organizerField(raw.from) ??
    asString(raw.sender) ??
    organizerField(raw.sender);
  return {
    id,
    subject,
    from,
    receivedAt:
      asString(raw.received_at) ??
      asString(raw.receivedAt) ??
      asString(raw.receivedDateTime) ??
      asString(raw.date),
    preview: asString(raw.preview) ?? asString(raw.bodyPreview) ?? asString(raw.snippet),
    webLink: asString(raw.web_link) ?? asString(raw.webLink),
    isRead: asBool(raw.is_read) ?? asBool(raw.isRead),
  };
};

const normalizeFileItem = (
  raw: Record<string, unknown>,
  index: number
): ConnectedFileItem | null => {
  const id =
    asString(raw.id) ??
    asString(raw.file_id) ??
    asString(raw.path_lower) ??
    `file-${index}`;
  const name =
    asString(raw.name) ??
    asString(raw.title) ??
    asString(raw.filename) ??
    asString(raw.path_display) ??
    'Untitled file';
  return {
    id,
    name,
    path:
      asString(raw.path_display) ??
      asString(raw.path) ??
      asString(raw.webUrl) ??
      asString(raw.web_url),
    mimeType: asString(raw.mimeType) ?? asString(raw.mime_type) ?? asString(raw['.tag']),
    webLink:
      asString(raw.webViewLink) ??
      asString(raw.web_link) ??
      asString(raw.webLink) ??
      asString(raw.webUrl) ??
      asString(raw.web_url) ??
      asString(raw.link),
    modifiedAt:
      asString(raw.modifiedTime) ??
      asString(raw.modified_at) ??
      asString(raw.client_modified) ??
      asString(raw.lastModifiedDateTime),
  };
};

const asUnknownArray = (value: unknown): unknown[] | undefined =>
  Array.isArray(value) ? (value as unknown[]) : undefined;

const readEventArray = (data: Record<string, unknown>): unknown[] | undefined => {
  for (const key of ['events', 'value', 'items', 'result']) {
    const value = data[key];
    const direct = asUnknownArray(value);
    if (direct) return direct;
    if (isRecord(value)) {
      const nested =
        asUnknownArray(value.events) ??
        asUnknownArray(value.value) ??
        asUnknownArray(value.items);
      if (nested) return nested;
    }
  }
  return undefined;
};

const readMessageArray = (data: Record<string, unknown>): unknown[] | undefined => {
  for (const key of ['messages', 'value', 'items', 'result']) {
    const value = data[key];
    const direct = asUnknownArray(value);
    if (direct) return direct;
    if (isRecord(value)) {
      const nested = asUnknownArray(value.messages) ?? asUnknownArray(value.value);
      if (nested) return nested;
    }
  }
  return undefined;
};

const readFileArray = (data: Record<string, unknown>): unknown[] | undefined => {
  for (const key of ['files', 'value', 'items', 'entries', 'matches', 'result']) {
    const value = data[key];
    const direct = asUnknownArray(value);
    if (direct) return direct;
    if (isRecord(value)) {
      const nested =
        asUnknownArray(value.files) ??
        asUnknownArray(value.value) ??
        asUnknownArray(value.items) ??
        asUnknownArray(value.matches);
      if (nested) return nested;
    }
  }
  return undefined;
};

export function parseConnectedCalendarPayload(
  data: Record<string, unknown> | undefined,
  input?: Record<string, unknown>
): ConnectedCalendarPayload | null {
  if (!data) return null;
  const rawEvents = readEventArray(data);
  if (!rawEvents) return null;
  const events = rawEvents
    .map((row, index) => (isRecord(row) ? normalizeCalendarEvent(row, index) : null))
    .filter((row): row is ConnectedCalendarEvent => row !== null);

  const calendarIdsRaw = data.calendar_ids ?? data.calendarIds;
  const calendarIds = Array.isArray(calendarIdsRaw)
    ? calendarIdsRaw
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        .map((id) => id.trim())
    : undefined;

  return {
    provider: asString(data.provider) ?? asString(data.connector),
    start: asString(data.start) ?? asString(input?.start) ?? asString(input?.start_time),
    end: asString(data.end) ?? asString(input?.end) ?? asString(input?.end_time),
    events,
    calendarIds: calendarIds && calendarIds.length > 0 ? calendarIds : undefined,
    warning: asString(data.warning),
  };
}

export function parseConnectedMailPayload(
  data: Record<string, unknown> | undefined,
  input?: Record<string, unknown>
): ConnectedMailPayload | null {
  if (!data) return null;
  const rawMessages = readMessageArray(data);
  if (!rawMessages) return null;
  const messages = rawMessages
    .map((row, index) => (isRecord(row) ? normalizeMailMessage(row, index) : null))
    .filter((row): row is ConnectedMailMessage => row !== null);
  return {
    provider: asString(data.provider) ?? asString(data.connector),
    query: asString(data.query) ?? asString(input?.query),
    messages,
  };
}

export function parseConnectedFilesPayload(
  data: Record<string, unknown> | undefined,
  input?: Record<string, unknown>
): ConnectedFilesPayload | null {
  if (!data) return null;
  const rawFiles = readFileArray(data);
  if (!rawFiles) return null;
  const files = rawFiles
    .map((row, index) => {
      if (!isRecord(row)) return null;
      // Dropbox wraps hits as { metadata: { metadata: file } } or { metadata: file }.
      const meta = row.metadata;
      const nested =
        isRecord(meta) && isRecord(meta.metadata)
          ? meta.metadata
          : isRecord(meta)
            ? meta
            : row;
      return normalizeFileItem(nested, index);
    })
    .filter((row): row is ConnectedFileItem => row !== null);
  return {
    provider: asString(data.provider) ?? asString(data.connector),
    query: asString(data.query) ?? asString(input?.query),
    files,
  };
}

export const CONNECTED_CALENDAR_TOOL_NAMES = [
  'integrations.calendar.list',
  'integrations.merge.calendar.list',
] as const;

export const CONNECTED_MAIL_TOOL_NAMES = [
  'integrations.mail.search',
  'integrations.merge.mail.search',
] as const;

export const CONNECTED_FILES_TOOL_NAMES = [
  'integrations.merge.files.search',
  'integrations.files.search',
] as const;
